import type {
  IBaseAttributes,
  IListOptions,
  IListParams,
  ListResponse,
  TConstraints,
} from '@feathr/rachis';
import { Collection, Model } from '@feathr/rachis';

import type { TChannelStats } from './stats_aggregated';

export interface IFlavors {
  ad_click?: number;
  ad_view?: number;
  conversation_opened?: number;
  conversation_popped?: number;
  conversation_response?: number;
  dashboard_link_click?: number;
  dashboard_view?: number;
  email_link_click?: number;
  email_not_sent_via_opt_out?: number;
  email_send?: number;
  email_view?: number;
  page_link_click?: number;
  page_view?: number;
  partner_message_link_click?: number;
  partner_message_send?: number;
  partner_message_view?: number;
  pinpoint_tracked_email_click?: number;
  bot_pinpoint_tracked_email_click?: number;
  pinpoint_tracked_email_complaint?: number;
  pinpoint_tracked_email_delivered?: number;
  pinpoint_tracked_email_hardbounce?: number;
  pinpoint_tracked_email_open?: number;
  bot_pinpoint_tracked_email_open?: number;
  pinpoint_tracked_email_rendering_failure?: number;
  pinpoint_tracked_email_send?: number;
  pinpoint_tracked_email_softbounce?: number;
  pinpoint_tracked_email_suppression?: number;
  zapier_imports?: number;
  zapier_updates?: number;
}

interface IDailyStatsMetaData {
  date: string;
  name: string;
  obj_id: string;
}

export interface IGoogleStats {
  num_clicks: number;
  num_conversions: number;
  spend: number;
  ctr: number;
  cpc: number;
  cpm: number;
  num_views: number;
}

export interface IDailyStats extends IBaseAttributes {
  metadata: IDailyStatsMetaData;
  flavors?: IFlavors;
  num_crumbs?: number;
  num_users_active?: number;
  num_users_new?: number;
  num_cookies_total?: number;
  num_emails_primary?: number;
  /** Includes additional email addresses on a person */
  num_emails_total?: number;
  num_users_total?: number;
  num_users_change?: number;
  num_conversions?: number;
  num_clicks?: number;
  num_clicks_active?: number;
  num_clicks_ad?: number;
  num_clicks_email?: number;
  num_clicks_new?: number;
  num_clicks_page?: number;
  num_views?: number;
  num_views_ad?: number;
  num_views_email?: number;
  num_views_page?: number;
  num_deliveries_email?: number;
  num_sends_email?: number;
  spend?: number;
  coverages?: {
    email: number;
    cookies: number;
  };
  unfiltered?: Partial<IDailyStats>;

  channels?: TChannelStats<IDailyStats>;

  // Email campaigns
  clicks_per_person?: number;
  email_open_rate?: number;
  hard_bounce_rate?: number;
  opens_per_person?: number;
  successful_delivery_rate?: number;
  unique_deliveries?: number;

  // Shared by email campaigns and accounts
  email_click_through_rate?: number;

  // Account stats
  ad_click_through_rate?: number;
  google?: IGoogleStats;
  num_ad_clicks?: number;
  num_ad_views?: number;
  num_email_bounces_and_complaints?: number;
  num_email_clicks?: number;
  num_email_opens?: number;
  num_email_sends?: number;
  num_persons_created?: number;
  num_persons_created_by_data_integration?: number;
  num_persons_created_by_import_and_integration?: number;
  num_persons_created_by_ad_campaign?: number;
  num_persons_created_by_import?: number;
  num_persons_created_by_pixel?: number;
  num_persons_with_ad_clicks?: number;
  num_persons_with_ad_views?: number;
  num_persons_with_page_link_clicks?: number;
  num_persons_with_page_views?: number;
  num_persons_with_email_clicks?: number;
  num_persons_with_email_opens?: number;
}

export class DailyStat extends Model<IDailyStats> {
  public readonly className = 'DailyStat';

  public get constraints(): TConstraints<IDailyStats> {
    return {};
  }
}

/** Models that can be used to get statistical data. */
export type TStatsListModel =
  | 'account'
  | 'goal'
  | 'campaign'
  | 'creative'
  | 'event'
  | 'flight'
  | 'form'
  | 'segment';

/**
 * Per-day stats document shape for donation forms. The generic
 * `GET /stats/?model=form&...` endpoint returns daily documents that may
 * include any of the donation-form-specific fields below. Mirrors
 * `IFormAggregatedStats` (see `stats_aggregated.ts`) at the daily level.
 * All fields are optional — the backend may omit zeros or fields that
 * aren't populated for a given day.
 */
export interface IFormDailyStats extends IDailyStats {
  average_donation?: number;
  credits_rate?: number;
  donations_count?: number;
  donations_credits?: number;
  donations_credits_count?: number;
  donations_recurring_count?: number;
  donations_recurring_total?: number;
  donations_total?: number;
  growth_credits?: number;
  num_submissions?: number;
  submission_rate?: number;
  unique_views?: number;
}

export interface IStatsListParams extends IListParams<IDailyStats> {
  model: TStatsListModel;
}

export class DailyStats extends Collection<DailyStat> {
  public getModel(attributes: Partial<IDailyStats>): DailyStat {
    return new DailyStat(attributes);
  }

  public getClassName(): string {
    return 'stats';
  }

  public override list(
    params: Partial<IStatsListParams>,
    options: Partial<IListOptions> = {},
    /** listId Use the useId() hook to generate a unique listID. */
    listId?: string,
  ): ListResponse<DailyStat> {
    return super.list(params, options, listId);
  }
}
